import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	CreateDealMeetingBody,
	DealMeeting,
	ListDealMeetingsParams,
	ListDealMeetingsResponse,
	ListMeetingsParams,
	ListMeetingsResponse,
	UpdateDealMeetingBody,
} from './types/deal'

export const dealMeetingsApi = createApi({
	reducerPath: 'dealMeetingsApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['DealMeeting'],
	endpoints: (builder) => ({
		/**
		 * Every meeting in the account, not one deal's.
		 *
		 * The tasks list shows meetings beside tasks, and it cannot ask per deal:
		 * it does not know which deals exist until it has the rows.
		 */
		listMeetings: builder.query<ListMeetingsResponse, ListMeetingsParams | void>({
			query: (params) => ({ url: '/crm/meetings', params: params ?? undefined }),
			providesTags: ['DealMeeting'],
		}),

		listDealMeetings: builder.query<ListDealMeetingsResponse, ListDealMeetingsParams>({
			query: ({ dealId, ...params }) => ({
				url: `/crm/deals/${dealId}/meetings`,
				params,
			}),
			providesTags: (_r, _e, { dealId }) => [{ type: 'DealMeeting', id: dealId }],
		}),

		createDealMeeting: builder.mutation<DealMeeting | null, CreateDealMeetingBody>({
			query: ({ dealId, ...body }) => ({
				url: `/crm/deals/${dealId}/meetings`,
				method: 'POST',
				body,
			}),
			invalidatesTags: (_r, _e, { dealId }) => [{ type: 'DealMeeting', id: dealId }],
		}),

		updateDealMeeting: builder.mutation<DealMeeting | null, UpdateDealMeetingBody>({
			query: ({ id, ...body }) => ({
				url: `/crm/meetings/${id}`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: ['DealMeeting'],
		}),

		deleteDealMeeting: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/crm/meetings/${id}`, method: 'DELETE' }),
			invalidatesTags: ['DealMeeting'],
		}),
	}),
})

export const {
	useListMeetingsQuery,
	useListDealMeetingsQuery,
	useCreateDealMeetingMutation,
	useUpdateDealMeetingMutation,
	useDeleteDealMeetingMutation,
} = dealMeetingsApi
